import { useEffect, useRef, useState } from "react";
import AccountButton from "../AccountButton";
import AccountInput from "../AccountInput";
import AccountSection from "../AccountSection";
import {
  addHospital,
  deleteHospital,
  getHospitals,
  updateHospital,
} from "../api/apiFunctions";
import { DisplayOnCardPill } from "../components/Pills";
import { t } from "../i18n";
import { getAuthToken } from "../utils/auth";
import { replaceNullWithPlaceholder } from "../utils";

export default function HospitalPage({ lang = "en", goToTab }) {
  const [search, setSearch] = useState("");
  const [selectedHospital, setSelectedHospital] = useState({
    name: "",
    npi: "",
    specialty: "",
    salId: "",
    service: "",
    address: "",
    phone: "",
    email: "",
  });
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [apiResults, setApiResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isHospitalLoading, setIsHospitalLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("info"); // "info", "success", "error"

  // Edit modal states
  const [showEditHospitalModal, setShowEditHospitalModal] = useState(false);
  const [hospitalToEdit, setHospitalToEdit] = useState(null);
  const [editHospitalData, setEditHospitalData] = useState({
    name: "",
    role: "",
    specialty: "",
    status: "active",
  });

  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [hospitalToDelete, setHospitalToDelete] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadHospitals();
  }, []);

  const loadHospitals = async () => {
    try {
      setLoading(true);
      setError("");
      const token = getAuthToken();

      if (!token) {
        setError("Please log in to view your hospitals");
        return;
      }

      const response = await getHospitals(token);

      if (response.success && response.data) {
        // Transform API data to match component structure
        // The data is directly an array, not nested under 'hospitals'
        const hospitalsData = Array.isArray(response.data)
          ? response.data
          : response.data.hospitals || [];
        console.log("Hospitals data to map:", hospitalsData);

        setHospitals(
          hospitalsData.map((hospital) => ({
            _id: hospital._id || hospital.id,
            name: hospital.name,
            role: hospital.role,
            specialty: hospital.speciality || hospital.specialty,
            npi: hospital.npiNumber || hospital.npi,
            status: hospital.status === "active" ? "current" : "past",
            displayOnCard: hospital.displayOnCard,
          })),
        );
      }
    } catch (error) {
      console.error("Error loading hospitals:", error);
      setError("Failed to load hospitals. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Hide dropdown if clicked outside
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch from NPI Org API
  useEffect(() => {
    const fetchHospitals = async () => {
      if (!search || search.length < 2) {
        setApiResults([]);
        setIsHospitalLoading(false);
        return;
      }
      setIsHospitalLoading(true);
      try {
        const url = `https://clinicaltables.nlm.nih.gov/api/npi_org/v3/search?terms=${encodeURIComponent(
          search,
        )}&count=10&df=NPI,name.full,provider_type,addr_practice.full,addr_practice.phone`;
        const res = await fetch(url);
        const data = await res.json();
        const results = (data[3] || []).map((row, idx) => ({
          npi: data[1][idx],
          name: row[1] || "",
          specialty: row[2] || "",
          address: row[3] || "",
          phone: row[4] || "",
        }));
        setApiResults(results);
        if (results.length > 0) setShowDropdown(true);
      } catch (e) {
        setApiResults([]);
        setShowDropdown(true);
      } finally {
        setIsHospitalLoading(false);
      }
    };
    const timeout = setTimeout(fetchHospitals, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  // Dropdown options
  const serviceOptions = [
    { value: "", label: t("choose", lang) },
    { value: "general", label: t("hospitalGeneral", lang) },
    { value: "specialty", label: t("hospitalSpecialty", lang) },
    { value: "rehab", label: t("rehabilitation", lang) },
    { value: "surgery", label: t("surgeryCenter", lang) },
  ];

  // Handlers
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    if (apiResults.length >= 0) setShowDropdown(true);
    else setShowDropdown(false);
  };
  const handleHospitalChange = (e) => {
    const { name, value } = e.target;
    setSelectedHospital((prev) => ({ ...prev, [name]: value }));
  };

  const handleHospitalSelect = (hospital) => {
    setSelectedHospital({
      name: hospital.name || "",
      npi: hospital.npi || "",
      specialty: hospital.specialty || "",
      service: hospital.specialty || "",
      address: hospital.address || "",
      phone: hospital.phone || "",
      email: "",
      salId: "",
    });
    setSearch("");
    setShowDropdown(false);
  };

  // Show custom alert
  const showAlert = (message, type = "info") => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlertModal(true);
  };

  // Add hospital to list
  const addHospitalToList = async () => {
    if (!selectedHospital.name || selectedHospital.name === "") {
      showAlert("Please select a hospital first", "error");
      return false;
    }

    // Check if hospital already exists in the list
    const existingHospital = hospitals.find(
      (hosp) => hosp.npi === selectedHospital.npi,
    );
    if (existingHospital) {
      showAlert("This hospital is already in your list", "error");
      return false;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) {
        setError("Please log in to add hospitals");
        return;
      }

      // Prepare data for API - map form fields to API schema
      const hospitalData = {
        name: selectedHospital.name,
        role: selectedHospital.service || selectedHospital.specialty,
        npiNumber: selectedHospital.npi,
        speciality: selectedHospital.specialty,
        phone: selectedHospital.phone,
        status: "active",
      };

      const response = await addHospital(hospitalData, token);

      if (response.success) {
        setSuccess("Hospital added successfully!");
        // Reload hospitals list
        await loadHospitals();

        // Clear the selected hospital form
        setSelectedHospital({
          name: "",
          npi: "",
          specialty: "",
          salId: "",
          service: "",
          address: "",
          phone: "",
          email: "",
        });
        setSearch("");

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
        return true; // Return true to indicate success
      }
      return false; // Return false if response was not successful
    } catch (error) {
      console.error("Error adding hospital:", error);
      setError(error.message || "Failed to add hospital. Please try again.");
      return false; // Return false to indicate failure
    } finally {
      setSaving(false);
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = (hospital) => {
    setHospitalToDelete(hospital);
    setShowDeleteModal(true);
  };

  // Remove hospital from list
  const removeHospital = async () => {
    if (!hospitalToDelete) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) {
        setError("Please log in to remove hospitals");
        return;
      }

      const response = await deleteHospital(hospitalToDelete._id, token);

      if (response.success) {
        setSuccess("Hospital removed successfully!");
        // Reload hospitals list
        await loadHospitals();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (error) {
      console.error("Error removing hospital:", error);
      setError(error.message || "Failed to remove hospital. Please try again.");
    } finally {
      setLoading(false);
      // Close modal
      setShowDeleteModal(false);
      setHospitalToDelete(null);
    }
  };

  // Edit hospital
  const editHospital = (hospital) => {
    setHospitalToEdit(hospital);
    setEditHospitalData({
      name: hospital.name,
      role: hospital.role,
      specialty: hospital.specialty,
      status: hospital.status === "current" ? "active" : "inactive",
    });
    setShowEditHospitalModal(true);
  };

  // Save edited hospital
  const saveEditedHospital = async () => {
    if (!hospitalToEdit || !editHospitalData.name.trim()) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) {
        setError("Please log in to update hospitals");
        return;
      }

      // Prepare data for API - map form fields to API schema
      const hospitalData = {
        name: editHospitalData.name,
        role: editHospitalData.role,
        npiNumber: hospitalToEdit.npi,
        speciality: editHospitalData.specialty,
        status: editHospitalData.status,
      };

      const response = await updateHospital(
        hospitalToEdit._id,
        hospitalData,
        token,
      );

      if (response.success) {
        setSuccess("Hospital updated successfully!");
        // Reload hospitals list
        await loadHospitals();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (error) {
      console.error("Error updating hospital:", error);
      setError(error.message || "Failed to update hospital. Please try again.");
    } finally {
      setLoading(false);
      // Close modal
      setShowEditHospitalModal(false);
      setHospitalToEdit(null);
      setEditHospitalData({
        name: "",
        role: "",
        specialty: "",
        status: "active",
      });
    }
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center p-2">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
      <span className="ml-2 text-xs text-[#837f90]">Searching...</span>
    </div>
  );

  // No results component
  const NoResultsMessage = ({ searchTerm }) => (
    <div className="p-3 text-center">
      <div className="text-xs text-[#837f90] mb-1">
        No doctors found for "{searchTerm}"
      </div>
      <div className="text-xs text-[#837f90] opacity-75">
        Try a different search term or check spelling
      </div>
    </div>
  );

  const showInfoColumn = hospitals.some((hosp) => hosp.displayOnCard);

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center p-4 md:p-8">
      <div className="w-full max-w-3xl bg-white rounded-xl shadow p-8 mt-4">
        {/* Clinics and Hospitals */}
        <AccountSection>{t("clinicsAndHospitals", lang)}</AccountSection>

        {/* Error and Success Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-blue-700 font-medium">
                Loading hospitals...
              </span>
            </div>
          </div>
        )}
        <div
          className="mb-6 flex items-center gap-2 relative"
          ref={dropdownRef}
        >
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder={t("findHospitalPlaceholder", lang)}
            className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
          />
          <span className="inline-flex items-center justify-center w-10 h-10 rounded bg-[#f7f8fa] border border-gray-200">
            {isHospitalLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
            ) : (
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="#837f90"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            )}
          </span>
          {showDropdown && (
            <div className="absolute left-0 top-12 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-64 overflow-y-auto">
              {isHospitalLoading ? (
                <LoadingSpinner />
              ) : apiResults.length > 0 ? (
                apiResults.map((hosp, idx) => (
                  <button
                    key={hosp.npi + idx}
                    className="w-full text-left px-4 py-2 hover:bg-[#f7f8fa] border-b border-gray-100 last:border-b-0"
                    onClick={() => handleHospitalSelect(hosp)}
                  >
                    <div className="font-semibold text-[#2d348f]">
                      {hosp.name}
                    </div>
                    <div className="text-xs text-[#837f90]">
                      NPI: {hosp.npi} &bull; {hosp.specialty}
                    </div>
                    <div className="text-xs text-[#837f90]">{hosp.address}</div>
                  </button>
                ))
              ) : search.length >= 2 ? (
                <NoResultsMessage searchTerm={search} />
              ) : null}
            </div>
          )}
        </div>

        {/* Current Institution Form */}
        <AccountSection>{t("currentInstitution", lang)}</AccountSection>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <AccountInput
            label={t("hospitalName", lang)}
            name="name"
            value={selectedHospital.name}
            onChange={handleHospitalChange}
          />
          <div>
            <label className="text-xs font-semibold text-[#837f90] mb-1 block">
              {t("medicalServiceProvided", lang)}
            </label>
            <select
              name="service"
              value={selectedHospital.service}
              onChange={handleHospitalChange}
              className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
            >
              {serviceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <AccountInput
            label={t("npiNumber", lang)}
            name="npi"
            value={selectedHospital.npi}
            onChange={handleHospitalChange}
          />
          <AccountInput
            label={t("address", lang)}
            name="address"
            value={selectedHospital.address}
            onChange={handleHospitalChange}
          />
          <AccountInput
            label={t("specialty", lang)}
            name="specialty"
            value={selectedHospital.specialty}
            onChange={handleHospitalChange}
          />
          <AccountInput
            label={t("phone", lang)}
            name="phone"
            value={selectedHospital.phone}
            onChange={handleHospitalChange}
            isPhoneInput
          />
          <AccountInput
            label={t("salId", lang)}
            name="salId"
            value={selectedHospital.salId}
            onChange={handleHospitalChange}
          />
          <AccountInput
            label={t("email", lang)}
            name="email"
            value={selectedHospital.email}
            onChange={handleHospitalChange}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <AccountButton
            className="md:w-1/3"
            disabled={saving}
            onClick={() => goToTab("prev")}
          >
            <span className="mr-2">&laquo;</span> {t("back", lang)}
          </AccountButton>
          <AccountButton
            className="md:w-1/3"
            onClick={() => addHospitalToList()}
            disabled={saving}
          >
            {saving ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t("saving", lang) || "Saving..."}
              </div>
            ) : (
              t("save", lang)
            )}
          </AccountButton>
          <AccountButton
            className="md:w-1/3"
            onClick={() => goToTab("next")}
            disabled={saving}
          >
            <>
              {t("next", lang)} <span className="ml-2">&raquo;</span>
            </>
          </AccountButton>
        </div>

        {/* My Institutions Table */}
        <AccountSection>{t("myInstitutions", lang)}</AccountSection>

        {/* Current Hospitals */}
        {hospitals.filter((hosp) => hosp.status === "current").length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-[#2d348f] mb-3 flex items-center gap-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              Current Hospitals (
              {hospitals.filter((hosp) => hosp.status === "current").length})
            </h3>
            <div className="overflow-x-auto max-w-[300px] sm:max-w-full">
              <table className="bg-white text-sm min-w-full">
                <thead>
                  <tr className="bg-green-50 text-[#837f90]">
                    {showInfoColumn && (
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("Info", lang)}
                      </th>
                    )}
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("hospitalName", lang)}
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("role", lang)}
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("npiNumber", lang)}
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("specialty", lang)}
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("actions", lang)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hospitals
                    .filter((hosp) => hosp.status === "current")
                    .map((hosp) => (
                      <tr key={hosp.npi} className="border-t border-gray-100">
                        {showInfoColumn && (
                          <td className="px-4 py-2 font-medium">
                            {hosp.displayOnCard && <DisplayOnCardPill />}
                          </td>
                        )}
                        <td className="px-4 py-2 font-medium">
                          {replaceNullWithPlaceholder(hosp.name)}
                        </td>
                        <td className="px-4 py-2">
                          {replaceNullWithPlaceholder(hosp.role)}
                        </td>
                        <td className="px-4 py-2">
                          {replaceNullWithPlaceholder(hosp.npi)}
                        </td>
                        <td className="px-4 py-2">
                          {replaceNullWithPlaceholder(hosp.specialty)}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-3 py-1 text-xs rounded-full bg-green-100 text-green-700">
                              Current
                            </span>
                            <button
                              onClick={() => editHospital(hosp)}
                              disabled={loading}
                              className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 ${
                                loading
                                  ? "text-gray-400 cursor-not-allowed border border-gray-400"
                                  : "text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-600"
                              }`}
                              title={t("edit", lang)}
                            >
                              {t("edit", lang)}
                            </button>
                            <button
                              onClick={() => showDeleteConfirmation(hosp)}
                              disabled={loading}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                loading
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "text-red-600 hover:text-red-900 hover:bg-red-50"
                              }`}
                              title="Remove Hospital"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Past Hospitals */}
        {hospitals.filter((hosp) => hosp.status === "past").length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-[#2d348f] mb-3 flex items-center gap-2">
              <span className="w-3 h-3 bg-gray-500 rounded-full"></span>
              Past Hospitals (
              {hospitals.filter((hosp) => hosp.status === "past").length})
            </h3>
            <div className="overflow-x-auto max-w-xs sm:max-w-full">
              <table className="bg-white text-sm min-w-full">
                <thead>
                  <tr className="bg-gray-50 text-[#837f90]">
                    {showInfoColumn && (
                      <th className="px-4 py-2 text-left font-semibold">
                        {t("Info", lang)}
                      </th>
                    )}
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("hospitalName", lang)}
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("role", lang)}
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("npiNumber", lang)}
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("specialty", lang)}
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">
                      {t("actions", lang)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hospitals
                    .filter((hosp) => hosp.status === "past")
                    .map((hosp) => (
                      <tr key={hosp.npi} className="border-t border-gray-100">
                        {showInfoColumn && (
                          <td className="px-4 py-2 font-medium">
                            {hosp.displayOnCard && <DisplayOnCardPill />}
                          </td>
                        )}
                        <td className="px-4 py-2 font-medium">
                          {replaceNullWithPlaceholder(hosp.name)}
                        </td>
                        <td className="px-4 py-2">
                          {replaceNullWithPlaceholder(hosp.role)}
                        </td>
                        <td className="px-4 py-2">
                          {replaceNullWithPlaceholder(hosp.npi)}
                        </td>
                        <td className="px-4 py-2">
                          {replaceNullWithPlaceholder(hosp.specialty)}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                              Past
                            </span>
                            <button
                              onClick={() => editHospital(hosp)}
                              disabled={loading}
                              className={`px-3 py-1 text-sm rounded-lg transition-all duration-200 ${
                                loading
                                  ? "text-gray-400 cursor-not-allowed border border-gray-400"
                                  : "text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-600"
                              }`}
                              title={t("edit", lang)}
                            >
                              {t("edit", lang)}
                            </button>
                            <button
                              onClick={() => showDeleteConfirmation(hosp)}
                              disabled={loading}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                loading
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "text-red-600 hover:text-red-900 hover:bg-red-50"
                              }`}
                              title="Remove Hospital"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Hospitals Message */}
        {hospitals.length === 0 && !loading && (
          <div className="text-center py-8 text-[#837f90]">
            <div className="text-lg mb-2">No hospitals in your list yet</div>
            <div className="text-sm">
              Search for a hospital above and save it to your list
            </div>
          </div>
        )}

        {/* Loading State for Hospitals List */}
        {loading && hospitals.length === 0 && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4962d8] mx-auto mb-4"></div>
            <div className="text-[#837f90] font-medium">
              Loading your hospitals...
            </div>
          </div>
        )}

        {/* Custom Alert Modal */}
        {showAlertModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
                      alertType === "success"
                        ? "bg-green-100"
                        : alertType === "error"
                          ? "bg-red-100"
                          : "bg-blue-100"
                    }`}
                  >
                    {alertType === "success" ? (
                      <svg
                        className="w-6 h-6 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : alertType === "error" ? (
                      <svg
                        className="w-6 h-6 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    )}
                  </div>
                  <h3
                    className={`text-lg font-semibold ${
                      alertType === "success"
                        ? "text-green-900"
                        : alertType === "error"
                          ? "text-red-900"
                          : "text-blue-900"
                    }`}
                  >
                    {alertType === "success"
                      ? "Success"
                      : alertType === "error"
                        ? "Error"
                        : "Information"}
                  </h3>
                </div>

                <p
                  className={`mb-6 ${
                    alertType === "success"
                      ? "text-green-700"
                      : alertType === "error"
                        ? "text-red-700"
                        : "text-blue-700"
                  }`}
                >
                  {alertMessage}
                </p>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAlertModal(false)}
                    className={`px-6 py-2 font-medium rounded-lg transition-colors ${
                      alertType === "success"
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : alertType === "error"
                          ? "bg-red-600 text-white hover:bg-red-700"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Hospital Modal */}
        {showEditHospitalModal && hospitalToEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <svg
                      className="w-6 h-6 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit Hospital Information
                  </h3>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hospital Name
                    </label>
                    <input
                      type="text"
                      value={editHospitalData.name}
                      onChange={(e) =>
                        setEditHospitalData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter hospital name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role/Specialty
                    </label>
                    <input
                      type="text"
                      value={editHospitalData.role}
                      onChange={(e) =>
                        setEditHospitalData((prev) => ({
                          ...prev,
                          role: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter role or specialty"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Specialty Details
                    </label>
                    <input
                      type="text"
                      value={editHospitalData.specialty}
                      onChange={(e) =>
                        setEditHospitalData((prev) => ({
                          ...prev,
                          specialty: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter specialty details"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={editHospitalData.status}
                      onChange={(e) =>
                        setEditHospitalData((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="active">Current Hospital</option>
                      <option value="inactive">Past Hospital</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setShowEditHospitalModal(false);
                      setHospitalToEdit(null);
                      setEditHospitalData({
                        name: "",
                        role: "",
                        specialty: "",
                        status: "active",
                      });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEditedHospital}
                    disabled={loading}
                    className={`flex-1 px-4 py-2 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      loading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {loading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && hospitalToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Remove Hospital
                  </h3>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600 mb-3">
                    Are you sure you want to remove this hospital from your
                    list? This action cannot be undone.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="font-semibold text-gray-900">
                      {hospitalToDelete.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {hospitalToDelete.specialty}
                    </div>
                    <div className="text-sm text-gray-500">
                      NPI: {hospitalToDelete.npi}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setHospitalToDelete(null);
                    }}
                    disabled={loading}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={removeHospital}
                    disabled={loading}
                    className={`flex-1 px-4 py-2 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
                      loading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    {loading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    )}
                    Remove Hospital
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
